import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { ImageOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import {
  getWeeklyReports,
  type WeeklyReportPhoto,
} from '@/api/weeklyReports';
import {
  getLayoutDetail,
  getZoneAnalyticsTab,
  type CleanupHistogram,
  type LayoutDetailRack,
  type TaskTimingHistogram,
} from '@/api/analytics';
import { listChecklists, type ChecklistDto } from '@/api/ops_tasks';
import type { Paginated } from '@/api/types';
import { RackMiniChart, RackDayLightbox } from './day/RackMiniGrid';
import { DayQueueCard } from './day/DayQueueCard';
import { StaffArrivalCard } from './day/StaffArrivalCard';
import { TableStateStrips } from './TableStateStrips';
import { WeekVisitsCard } from './week/WeekVisitsCard';
import { WeekOtherCard } from './week/WeekOtherCard';

const CARD_TITLE = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

// HH:MM:SS-ish duration in the "N мин M с" shape the Stats → Tables tab uses.
function fmtDuration(s: number | null): string {
  if (s == null) return '—';
  const mins = Math.floor(s / 60);
  const secs = Math.round(s % 60);
  if (mins === 0) return `${secs} с`;
  if (secs === 0) return `${mins} мин`;
  return `${mins} мин ${secs} с`;
}

// ── week-date helpers ──────────────────────────────────────────────────────────

function isoOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return isoOf(dt);
}
function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
  });
}

const DOT = 'bg-emerald-500';

// ── Выкладка: rack × day chart grid + drops + percentile photos ────────────────
//
// The old numeric section ("выкладка 62% · было 60%") told the owner nothing —
// he asked for the charts themselves: a grid of compact per-rack shelf charts,
// Mon–Sun columns, one row per rack; a cell click opens the same photos-first
// fullscreen the day report uses. Below the grid: the week's longest low-fill
// episodes as connected photo series, then the per-rack percentile trio.

const PRIORITY_RACK_TYPES = ['Готовая еда', 'Выпечка', 'Десерты'];

// React.memo + a stable onPick: opening/closing a cell's fullscreen must not
// re-render 7×racks recharts instances (that was the visible click/close lag).
const WeekLayoutGrid = React.memo(function WeekLayoutGrid({
  zoneId,
  monday,
  onPick,
}: {
  zoneId: string;
  monday: string;
  onPick: (rack: LayoutDetailRack, day: string) => void;
}) {
  const { t } = useT();
  // Racks list — one cheap request; each cell then fetches its own slice.
  const { data } = useSWR(
    zoneId ? ['report:week-racks', zoneId, monday] : null,
    () =>
      getLayoutDetail(zoneId, monday, monday, undefined, '15min', {
        photoMode: 'even',
        photoCount: 1,
      }).then((r) => r.data),
    { revalidateOnFocus: false },
  );

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(monday, i)), [monday]);

  // Which cells actually have data — all-empty rows and all-empty day columns
  // hide instead of painting a wall of «нет данных».
  const [cellHasData, setCellHasData] = useState<Record<string, boolean>>({});
  const reportCell = useCallback((rackId: number, day: string, has: boolean) => {
    const key = `${rackId}|${day}`;
    setCellHasData((m) => (m[key] === has ? m : { ...m, [key]: has }));
  }, []);
  useEffect(() => setCellHasData({}), [zoneId, monday]);

  const racks = data?.racks ?? [];
  // Priority categories first; everything else hides behind an expander.
  const priorityRacks = racks.filter((r) => PRIORITY_RACK_TYPES.includes((r.rack_type ?? '').trim()));
  const otherRacks = racks.filter((r) => !PRIORITY_RACK_TYPES.includes((r.rack_type ?? '').trim()));
  const [showOthers, setShowOthers] = useState(false);

  if (!data) return <div className="h-48 animate-pulse rounded-xl bg-muted" />;
  if (!racks.length) return null;

  const baseRacks = priorityRacks.length ? priorityRacks : racks;
  const shownRacks = showOthers && priorityRacks.length ? [...priorityRacks, ...otherRacks] : baseRacks;

  const rowEmpty = (rackId: number) =>
    days.every((d) => cellHasData[`${rackId}|${d}`] === false);
  const colEmpty = (d: string) =>
    shownRacks.every((r) => cellHasData[`${r.id}|${d}`] === false);

  const visibleDays = days.filter((d) => !colEmpty(d));
  const visibleRacks = shownRacks.filter((r) => !rowEmpty(r.id));
  const gridStyle = { gridTemplateColumns: `repeat(${visibleDays.length}, minmax(0, 1fr))` };

  return (
    <div className="overflow-x-auto pb-1">
      <div className="space-y-2" style={{ minWidth: visibleDays.length * 140 }}>
        <div className="grid gap-1.5" style={gridStyle}>
          {visibleDays.map((d) => (
            <div key={d} className="text-center text-[11px] font-medium text-muted-foreground">
              {dayLabel(d)}
            </div>
          ))}
        </div>
        {visibleRacks.map((r) => (
          <div key={r.id} className="space-y-1">
            <div className="text-xs font-medium text-foreground">{r.name}</div>
            <div className="grid gap-1.5" style={gridStyle}>
              {visibleDays.map((d) => (
                <RackMiniChart
                  key={d}
                  zoneId={zoneId}
                  from={d}
                  to={d}
                  rackId={r.id}
                  title=""
                  height={80}
                  mini
                  onOpen={() => onPick(r, d)}
                  onData={(has) => reportCell(r.id, d, has)}
                />
              ))}
            </div>
          </div>
        ))}
        {/* Hidden cells still need to REPORT (else a hidden column could never
            come back when its data lands) — render the pruned cells 0-height. */}
        <div className="hidden">
          {shownRacks.flatMap((r) =>
            days
              .filter((d) => !visibleDays.includes(d))
              .map((d) => (
                <RackMiniChart
                  key={`${r.id}|${d}`}
                  zoneId={zoneId}
                  from={d}
                  to={d}
                  rackId={r.id}
                  title=""
                  height={1}
                  mini
                  onData={(has) => reportCell(r.id, d, has)}
                />
              )),
          )}
        </div>
        {priorityRacks.length > 0 && otherRacks.length > 0 && (
          <button
            type="button"
            onClick={() => setShowOthers((v) => !v)}
            className="text-xs font-medium text-primary hover:underline"
          >
            {showOthers
              ? t('report.week.hideOtherRacks')
              : t('report.week.showOtherRacks', { racks: otherRacks.map((r) => r.name).join(', ') })}
          </button>
        )}
      </div>
    </div>
  );
});

/** The week's longest low-fill episodes: ring on the first frame, primary
 *  connectors — the same visual language as the day report's uncleaned-table
 *  chains. */
function WeekDrops({ photos }: { photos: WeeklyReportPhoto[] }) {
  const { t } = useT();
  const [open, setOpen] = useState<number | null>(null);
  const withImg = photos.filter((p) => p.image_url);

  const drops = useMemo(() => {
    const byIdx = new Map<number, WeeklyReportPhoto[]>();
    for (const p of photos) {
      const k = p.drop ?? 0;
      if (!byIdx.has(k)) byIdx.set(k, []);
      byIdx.get(k)!.push(p);
    }
    return [...byIdx.entries()].sort((a, b) => a[0] - b[0]).map(([, ph]) => ph);
  }, [photos]);

  if (!withImg.length) return null;

  const idxOf = (p: WeeklyReportPhoto) => withImg.findIndex((w) => w.image_url === p.image_url);
  const captions = withImg.map((p) => {
    const at = p.at
      ? new Date(p.at).toLocaleString('ru-RU', { weekday: 'short', hour: '2-digit', minute: '2-digit' })
      : '';
    return `${p.rack_name ?? ''} · ${p.caption}${p.fill_pct != null ? ` · ${p.fill_pct}%` : ''}${at ? ` · ${at}` : ''}`;
  });

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <div className="text-xs font-semibold text-foreground">{t('report.week.dropsTitle')}</div>
      <div className="flex flex-wrap items-start gap-3">
        {/* Each episode in its OWN bordered box — side-by-side chains with a
            bare gap read as one long chain (the inter-chain gap was nearly
            the same width as the intra-chain connectors). */}
        {drops.map((ph, di) => {
          const first = ph[0];
          return (
            <div key={di} className="space-y-1 rounded-lg border border-border p-2">
              <div className="flex flex-wrap items-center gap-y-2">
                {ph.map((p, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="h-0.5 w-4 shrink-0 bg-primary sm:w-6" />}
                    <div
                      className={cn(
                        'flex h-24 shrink-0 items-center justify-center overflow-hidden rounded bg-muted sm:h-28',
                        i === 0 && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
                      )}
                    >
                      {p.image_url ? (
                        <button
                          type="button"
                          onClick={() => {
                            const li = idxOf(p);
                            if (li >= 0) setOpen(li);
                          }}
                          className="block h-full"
                        >
                          <img src={p.image_url} alt="" loading="lazy" className="block h-full w-auto max-w-none cursor-zoom-in object-contain" />
                        </button>
                      ) : (
                        <ImageOff className="size-4 text-muted-foreground/40" />
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <div className="truncate text-[11px] font-medium text-foreground">
                {first?.rack_name}
                {first?.at && (
                  <span className="text-muted-foreground">
                    {' · '}
                    {new Date(first.at).toLocaleDateString('ru-RU', { weekday: 'short' })}
                  </span>
                )}
                {first?.drop_minutes != null && (
                  <span className="text-muted-foreground"> · {t('report.week.dropLabel', { mins: first.drop_minutes })}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {open != null && (
        <ImageLightbox srcs={withImg.map((w) => w.image_url)} index={open} alt="" captions={captions} onClose={() => setOpen(null)} onNavigate={setOpen} />
      )}
    </div>
  );
}

/** Per-rack percentile trio (25/50/75): each rack is a compact GROUP of
 *  natural-width photos with ONE group caption (the rack name); groups flow
 *  onto the same line when they fit. The longest-incident photo is excluded —
 *  it duplicated the Самые долгие просадки panel right above. */
function WeekPercentiles({ photos }: { photos: WeeklyReportPhoto[] }) {
  const { t } = useT();
  const [open, setOpen] = useState<number | null>(null);
  // drop_minutes marks the legacy longest-incident quartet member — hide it
  // here (older stored reports still carry it).
  const trio = photos.filter((p) => p.drop_minutes == null);
  const withImg = trio.filter((p) => p.image_url);
  if (!withImg.length) return null;

  const byRack = new Map<string, WeeklyReportPhoto[]>();
  for (const p of trio) {
    const k = p.rack_name ?? '—';
    if (!byRack.has(k)) byRack.set(k, []);
    byRack.get(k)!.push(p);
  }

  const idxOf = (p: WeeklyReportPhoto) => withImg.findIndex((w) => w.image_url === p.image_url);
  // Full percentile caption in the lightbox; no fill % — a wrong detection
  // reads as a wrong promise there.
  const captions = withImg.map((p) => `${p.rack_name ?? ''} · ${p.caption}`);

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <div className="text-xs font-semibold text-foreground">{t('report.week.percentilesTitle')}</div>
      <div className="flex max-h-96 flex-wrap gap-x-8 gap-y-3 overflow-y-auto pr-1 thin-scrollbar">
        {[...byRack.entries()].map(([rack, ph]) => (
          <div key={rack} className="space-y-1">
            <div className="flex items-end gap-1.5">
              {ph.map((p, i) => (
                <div key={i}>
                  <div className="flex h-28 items-center justify-center overflow-hidden rounded">
                    {p.image_url ? (
                      <button
                        type="button"
                        onClick={() => {
                          const li = idxOf(p);
                          if (li >= 0) setOpen(li);
                        }}
                        className="block h-full"
                      >
                        <img src={p.image_url} alt="" loading="lazy" className="block h-full w-auto max-w-none cursor-zoom-in object-contain" />
                      </button>
                    ) : (
                      <div className="flex h-full w-16 items-center justify-center rounded bg-muted">
                        <ImageOff className="size-4 text-muted-foreground/40" />
                      </div>
                    )}
                  </div>
                  <div className="mt-0.5 line-clamp-1 max-w-40 text-[10px] text-muted-foreground">
                    {p.caption}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-[11px] font-medium text-foreground">{rack}</div>
          </div>
        ))}
      </div>
      {open != null && (
        <ImageLightbox srcs={withImg.map((w) => w.image_url)} index={open} alt="" captions={captions} onClose={() => setOpen(null)} onNavigate={setOpen} />
      )}
    </div>
  );
}

// ── tables card (Stats → Tables phrasing, not the useless "столы 0.1шт/день") ──

const HIST_SEGMENTS: [keyof CleanupHistogram, string, string][] = [
  ['le_2m', '#22c55e', '≤2 мин'],
  ['2_5m', '#84cc16', '2–5 мин'],
  ['5_15m', '#f59e0b', '5–15 мин'],
  ['gt_15m', '#ef4444', '>15 мин'],
];

/** Shared segmented distribution bar: colored segments + a legend row + a
 *  custom hover tooltip (count + share). No native `title` — system tooltips
 *  are slow and unstyled. */
function SegBar({ segs }: { segs: Array<{ label: string; color: string; count: number }> }) {
  const [hover, setHover] = useState<{ x: number; y: number; label: string; count: number } | null>(null);
  const total = segs.reduce((s, b) => s + b.count, 0);
  if (total === 0) return null;
  const visible = segs.filter((b) => b.count > 0);
  return (
    <div className="space-y-1.5">
      <div className="flex h-4 overflow-hidden rounded bg-muted">
        {visible.map((b) => (
          <div
            key={b.label}
            style={{ flexGrow: b.count, background: b.color }}
            onMouseMove={(e) => setHover({ x: e.clientX, y: e.clientY, label: b.label, count: b.count })}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {visible.map((b) => (
          <span key={b.label} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: b.color }} />
            {b.label} · {b.count}
          </span>
        ))}
      </div>
      {hover && (
        <div
          className="pointer-events-none fixed z-50 rounded-lg border border-border bg-card px-2 py-1 text-xs text-foreground shadow-md"
          style={
            hover.x > window.innerWidth - 200
              ? { right: window.innerWidth - hover.x + 12, top: hover.y + 12 }
              : { left: hover.x + 12, top: hover.y + 12 }
          }
        >
          {hover.label}: {hover.count} ({Math.round((hover.count / total) * 100)}%)
        </div>
      )}
    </div>
  );
}

function CleanupBar({ hist }: { hist: CleanupHistogram }) {
  return (
    <SegBar
      segs={HIST_SEGMENTS.map(([k, color, label]) => ({ label, color, count: hist[k] ?? 0 }))}
    />
  );
}

/** Small ± dynamics caption vs the previous week (green = improved). */
function WeekDelta({
  delta,
  betterWhenLower = false,
  fmt,
}: {
  delta: number | null | undefined;
  betterWhenLower?: boolean;
  fmt: (abs: number) => string;
}) {
  if (delta == null || delta === 0) return null;
  const good = betterWhenLower ? delta < 0 : delta > 0;
  return (
    <div className={cn('text-[11px] font-medium tabular-nums', good ? 'text-emerald-600' : 'text-red-500')}>
      {delta > 0 ? '+' : '−'}
      {fmt(Math.abs(delta))} vs пр. неделя
    </div>
  );
}

function WeekTablesCard({ zoneId, from, to }: { zoneId: string; from: string; to: string }) {
  const { t } = useT();
  const { data } = useSWR(
    zoneId ? ['report:week-tables', zoneId, from, to] : null,
    () => getZoneAnalyticsTab(zoneId, 'tables', from, to, 'day').then((r) => r.data.tables),
    { revalidateOnFocus: false },
  );

  const median = data?.median_cleanup_s ?? null;
  const p90 = data?.p90_cleanup_s ?? null;
  const hasData = median != null || p90 != null;

  // No cleanup stats (loading, not configured, or a clean week) → nothing:
  // the «Состояние столов за неделю» strips card carries the section, so an
  // empty-state card here was pure noise.
  if (!hasData) return null;

  return (
    <Card>
      <CardContent className="space-y-2.5 p-3">
        <div className="flex items-center gap-2">
          <span className={cn('size-2.5 shrink-0 rounded-full', DOT)} />
          <span className="text-sm font-semibold text-foreground">Столы</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-2xl font-bold tabular-nums text-foreground">{fmtDuration(median)}</div>
            <div className="text-xs text-muted-foreground">{t('analytics.tables.medianLabel')}</div>
            <WeekDelta delta={data?.delta} betterWhenLower fmt={(s) => `${Math.max(1, Math.round(s / 60))} мин`} />
          </div>
          <div>
            <div className="text-2xl font-bold tabular-nums text-foreground">{fmtDuration(p90)}</div>
            <div className="text-xs text-muted-foreground">{t('analytics.tables.p90Label')}</div>
          </div>
        </div>
        {data?.cleanup_histogram && (
          <div className="pt-1">
            <div className="mb-1 text-xs text-muted-foreground">{t('analytics.tables.histLabel')}</div>
            <CleanupBar hist={data.cleanup_histogram} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Timing distribution of the week's tasks, INCLUDING the ones never done.
// Colours per Alexey 2026-07-21: late <2 h = YELLOW, late >2 h = ORANGE,
// «не сделано» = RED (early/on-time stay green/emerald). `hist` is null when
// nothing was done — the не-сделано segment must still show, so it degrades to
// zeros rather than hiding the bar.
function TimingBar({ hist, notDone }: { hist: TaskTimingHistogram | null; notDone: number }) {
  return (
    <SegBar
      segs={[
        { label: 'Заранее', color: '#22c55e', count: hist?.early ?? 0 },
        { label: 'В срок', color: '#34d399', count: hist?.on_time ?? 0 },
        { label: 'Опоздание < 2 ч', color: '#facc15', count: hist?.late_short ?? 0 },
        { label: 'Опоздание > 2 ч', color: '#fb923c', count: hist?.late_long ?? 0 },
        { label: 'Не сделано', color: '#ef4444', count: notDone },
      ]}
    />
  );
}

// Task completion summary — % done, % on-time, done/total, and the
// "когда выполняли относительно дедлайна" bar (Stats → Tasks data).
function WeekTasksCard({ zoneId, from, to }: { zoneId: string; from: string; to: string }) {
  const { t } = useT();
  const { data } = useSWR(
    zoneId ? ['report:week-tasks', zoneId, from, to] : null,
    () => getZoneAnalyticsTab(zoneId, 'tasks', from, to, 'day').then((r) => r.data.tasks),
    { revalidateOnFocus: false },
  );
  if (!data || data.total === 0) return null;

  return (
    <Card>
      <CardContent className="space-y-2.5 p-3">
        <div className="flex items-center gap-2">
          <span className={cn('size-2.5 shrink-0 rounded-full', DOT)} />
          <span className="text-sm font-semibold text-foreground">Задачи</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-2xl font-bold tabular-nums text-foreground">
              {data.completion_rate != null ? `${data.completion_rate}%` : '—'}
            </div>
            <div className="text-xs text-muted-foreground">{t('analytics.tasks.statLabel')}</div>
            <WeekDelta delta={data.delta_pct} fmt={(v) => `${Math.round(v)}%`} />
          </div>
          <div>
            <div className="text-2xl font-bold tabular-nums text-foreground">
              {data.on_time_rate != null ? `${data.on_time_rate}%` : '—'}
            </div>
            <div className="text-xs text-muted-foreground">{t('analytics.tasks.onTimeLabel')}</div>
            <WeekDelta delta={data.on_time_delta_pct} fmt={(v) => `${Math.round(v)}%`} />
          </div>
          <div>
            <div className="text-2xl font-bold tabular-nums text-foreground">
              {data.done}/{data.total}
            </div>
            <div className="text-xs text-muted-foreground">{t('analytics.tasks.completedLabel')}</div>
          </div>
        </div>
        {(data.timing_histogram || data.total > data.done) && (
          <div className="pt-1">
            <div className="mb-1 text-xs text-muted-foreground">{t('analytics.tasks.timingLabel')}</div>
            <TimingBar hist={data.timing_histogram} notDone={Math.max(0, data.total - data.done)} />
          </div>
        )}
        {/* What was NOT done this week — same digest the stats page shows. */}
        {(data.chronic_fail?.length ?? 0) > 0 && (
          <div className="pt-1">
            <div className="mb-1 text-xs text-muted-foreground">{t('report.week.tasksNotDone')}</div>
            <ul className="space-y-1">
              {data.chronic_fail.slice(0, 6).map((it) => (
                <li key={it.checklist_item_id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="min-w-0 flex-1 truncate text-foreground">
                    {it.task_text}
                    {it.due_time ? <span className="text-muted-foreground"> · {it.due_time}</span> : null}
                  </span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {it.done}/{it.total}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── week report ────────────────────────────────────────────────────────────────

// No numbers, no "details" footnotes: the owner reads the charts. Выкладка is
// a rack × day chart grid with drop/percentile photos, Очередь is the
// signature full-week chart, Столы = cleanup stats + the state strips.
// The week cursor (`monday`) is owned by OverviewReportPage and driven by the
// shared header date stepper — same pattern as DailyReportView's `date`.
export function WeekReport({ zoneId, monday }: { zoneId: string; monday: string }) {
  const { t } = useT();
  const sunday = addDays(monday, 6);
  const [openCell, setOpenCell] = useState<{ rack: LayoutDetailRack; day: string } | null>(null);
  // Stable across renders so the memoized grid never re-renders on open/close.
  const onPickCell = useCallback(
    (rack: LayoutDetailRack, day: string) => setOpenCell({ rack, day }),
    [],
  );

  const { data, isLoading, error } = useSWR(
    zoneId ? ['report:week', zoneId, monday] : null,
    () => getWeeklyReports(zoneId, monday, monday).then((r) => r.results[0] ?? null),
    // Week stepping keeps the previous week's content on screen while the
    // next loads — the page doesn't collapse, so scroll survives.
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  // Checklists gate — SAME signal as Part-1 B1: a checklist counts as configured
  // only when it has ≥1 task item. The weekly `tasks` section's `configured` flag
  // is always true (the BE only flips it off on error), so it would otherwise
  // show a "—" Задачи card on a zone with an empty checklist. Gate on actual
  // task items instead, exactly like the dashboard/sidebar do.
  const { data: checklistsData } = useSWR<Paginated<ChecklistDto>>(
    zoneId ? ['report:week-checklists', zoneId] : null,
    async () => (await listChecklists(zoneId, 100)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const tasksConfigured =
    !!checklistsData && checklistsData.results.some((c) => (c.items?.length ?? 0) > 0);

  const photos = data?.photo_highlights ?? {};
  const layoutConfigured = data?.sections?.layout?.configured !== false;
  const queueConfigured = data?.sections?.queue?.configured !== false;

  return (
    <div className="space-y-3">
      {!data && isLoading && <div className="h-40 animate-pulse rounded-xl bg-muted" />}
      {error && !data && (
        <Card><CardContent className="p-0"><ServerStatusNotice variant="card" error={error} /></CardContent></Card>
      )}
      {!isLoading && !error && !data && (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">{t('report.noWeek')}</CardContent></Card>
      )}
      {data && (
        <>
          {layoutConfigured && (
            <Card>
              <CardHeader className="pt-4 pb-2">
                <CardTitle className={CARD_TITLE}>{t('report.week.layoutTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <WeekLayoutGrid zoneId={zoneId} monday={monday} onPick={onPickCell} />
                {(photos.layout_drops?.length ?? 0) > 0 && <WeekDrops photos={photos.layout_drops!} />}
                {(photos.layout?.length ?? 0) > 0 && <WeekPercentiles photos={photos.layout!} />}
              </CardContent>
            </Card>
          )}

          {queueConfigured && (
            <DayQueueCard
              zoneId={zoneId}
              day={monday}
              to={sunday}
              bucketMs={15 * 60 * 1000}
              title={t('report.week.queueTitle')}
            />
          )}
          {/* Приход к открытию за неделю (#1324) — только у филиалов с включённой функцией. */}
          <StaffArrivalCard zoneId={zoneId} from={monday} to={sunday} compact />

          {/* Столы — Stats → Tables median/p90, self-hides when no tables. */}
          <WeekTablesCard zoneId={zoneId} from={monday} to={sunday} />
          <TableStateStrips
            zoneId={zoneId}
            from={monday}
            to={sunday}
            title={t('report.tables.stripsWeekTitle')}
            experimental
            deepLink
            thumbs={false}
          />

          {tasksConfigured && <WeekTasksCard zoneId={zoneId} from={monday} to={sunday} />}

          {/* Визиты (VISITS-3, #176) — self-hides on the feature flag / no visits. */}
          <WeekVisitsCard zoneId={zoneId} from={monday} to={sunday} />

          {/* «Другое» (Alexey 16.09) — новые отзывы за неделю, ссылками. */}
          <WeekOtherCard zoneId={zoneId} from={monday} to={sunday} />
          <p className="pt-1 text-right text-xs text-muted-foreground">
            {t('report.generatedAt', { time: new Date(data.generated_at).toLocaleString('ru-RU') })}
          </p>

          {openCell && (
            <RackDayLightbox
              zoneId={zoneId}
              day={openCell.day}
              rackId={openCell.rack.id}
              rackName={openCell.rack.name}
              onClose={() => setOpenCell(null)}
            />
          )}
        </>
      )}
    </div>
  );
}
